import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { RequestResults } from "../../common/RequestStatus";
import { fetchSchedulerInterval, updateSchedulerInterval, runUnpaidCleanupNow } from "./schedulerSettingsAsyncThunk";

export interface SchedulerIntervalData {
  id: number;
  openToPendingMinutes: number;
  pendingToReviewMinutes: number;
  reviewToInProcessMinutes: number;
  unpaidCleanupEnabled: boolean;
  incompleteDraftDays: number;
  unpaidClaimDays: number;
  lastCleanupRunAt: string | null;
  lastCleanupDeletedCount: number;
  recentPaymentHighlightHours: number;
}

interface SchedulerSettingsState {
  data: SchedulerIntervalData;
  status: RequestResults;
  loading: RequestResults;
  cleanupRunning: RequestResults;
  error: string | null;
}

const initialState: SchedulerSettingsState = {
  data: {
    id: 0,
    openToPendingMinutes: 0,
    pendingToReviewMinutes: 0,
    reviewToInProcessMinutes: 0,
    unpaidCleanupEnabled: false,
    incompleteDraftDays: 7,
    unpaidClaimDays: 30,
    lastCleanupRunAt: null,
    lastCleanupDeletedCount: 0,
    recentPaymentHighlightHours: 26,
  },
  status: RequestResults.idle,
  loading: RequestResults.idle,
  cleanupRunning: RequestResults.idle,
  error: null,
};

const schedulerSettingsSlice = createSlice({
  name: "schedulerSettings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchedulerInterval.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchSchedulerInterval.fulfilled, (state, action: PayloadAction<SchedulerIntervalData>) => {
        state.status = RequestResults.success;
        state.data = { ...state.data, ...action.payload };
      })
      .addCase(fetchSchedulerInterval.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error((action.payload as string) || "Failed to fetch scheduler settings");
        state.error = (action.payload as string) || "Failed to fetch scheduler settings";
      })
      .addCase(updateSchedulerInterval.pending, (state) => {
        state.loading = RequestResults.pending;
      })
      .addCase(updateSchedulerInterval.fulfilled, (state, action) => {
        state.data = { ...state.data, ...action.payload };
        toast.success("Scheduler settings saved successfully");
        state.loading = RequestResults.success;
      })
      .addCase(updateSchedulerInterval.rejected, (state, action) => {
        const errMsg = (action.payload as string) || "Failed to update scheduler settings";
        state.error = errMsg;
        toast.error(errMsg);
        state.loading = RequestResults.failed;
      })
      .addCase(runUnpaidCleanupNow.pending, (state) => {
        state.cleanupRunning = RequestResults.pending;
      })
      .addCase(runUnpaidCleanupNow.fulfilled, (state, action: PayloadAction<{ message: string; totalDeleted: number }>) => {
        state.cleanupRunning = RequestResults.success;
        toast.success(action.payload.message);
        state.data.lastCleanupRunAt = new Date().toISOString();
        state.data.lastCleanupDeletedCount = action.payload.totalDeleted;
      })
      .addCase(runUnpaidCleanupNow.rejected, (state, action) => {
        state.cleanupRunning = RequestResults.failed;
        toast.error((action.payload as string) || "Manual cleanup failed");
      });
  },
});

export const selectSchedulerSettings = (state: RootState) => state.schedulerSettings;

export default schedulerSettingsSlice.reducer;
